import type { ReactElement } from "react";
import { Link } from "@tanstack/react-router";
import type { Project } from "../lib/useProjects";

function timeAgo(iso: string): string {
	const diff = Date.now() - new Date(iso).getTime();
	const minutes = Math.floor(diff / 60000);
	if (minutes < 1) return "just now";
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `${days}d ago`;
	return new Date(iso).toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
	});
}

export function ProjectCard({ project }: { project: Project }): ReactElement {
	const isSynthesizing = project.synthesis_due_at !== null;
	const hasSynthesis = project.last_synthesized_at !== null;

	return (
		<Link
			to="/project/$projectId"
			params={{ projectId: project.id }}
			className="border-line bg-surface hover:border-line-strong group block rounded-lg border p-4 shadow-sm transition-colors"
		>
			<div className="mb-3 flex items-start justify-between gap-2">
				<h3 className="text-fg-strong text-sm leading-snug font-medium">
					{project.name}
				</h3>
				{isSynthesizing && (
					<span className="bg-accent-wash text-accent shrink-0 animate-pulse rounded-full px-1.5 py-0.5 text-[9px] leading-none font-medium tracking-wider uppercase">
						synthesizing
					</span>
				)}
			</div>

			{project.description && (
				<p className="text-fg-tertiary mb-3 line-clamp-2 text-xs leading-relaxed">
					{project.description}
				</p>
			)}

			<div className="text-fg-ghost flex items-center gap-3 text-[11px]">
				<span>
					{project.document_count} doc
					{project.document_count !== 1 ? "s" : ""}
				</span>
				{project.source_count > 0 && (
					<span>
						{project.source_count} source
						{project.source_count !== 1 ? "s" : ""}
					</span>
				)}
				<span className="ml-auto">
					{hasSynthesis
						? `Synthesized ${timeAgo(project.last_synthesized_at!)}`
						: `Updated ${timeAgo(project.updated_at)}`}
				</span>
			</div>
		</Link>
	);
}
